import type { TutorAuditAttributes, UserAttributes } from '@/server/models'

import SessionQ from '@/queries/SessionQ'
import { SQL, type SQLFragment } from '@/client/query'
import {
  COACH_ESCALATION_SLUG,
  EXPERIENCED_AUDIT_LEVELS,
  SAFETY_REPORT_SLUG,
} from '@/client/services/sessionAudit/constants'
import {
  AuditRating,
  type AuditRubricResponse,
  ItemResponse,
  type MarsKey,
  TutorAuditorLevel,
} from '@/client/services/sessionAudit/types'

import AuditorNoteQ from './AuditorNoteQ'
import {
  type AssociationDefinitions,
  type GroupBy,
  type IncludeAssociations,
  includeAssociationsToSql,
  makeQueryModelV2,
  type QueryOrder,
  queryGroupByToSQL,
  queryOrderToSQL,
} from './common'
import TutorAuditFeaturedQ from './TutorAuditFeaturedQ'
import UserQ from './UserQ'
import WorldQ from './WorldQ'

const TutorAuditQ = {
  ...makeQueryModelV2<TutorAuditAttributes>('tutor_audit')([
    'id',
    'user_id',
    'auditor_id',
    'description',
    'details',
    'internal_notes',
    'session_id',
    'rating',
    'created_at',
    'is_peer_review',
    'audit_level',
    'volunteer_activity_id',
    'reply_comment',
    'reply_submitted_at',
    'reply_rating',
    'verifier_id',
    'verified_at',
    'duration_seconds',
    'deleted_at',
  ]),

  associations: (): AssociationDefinitions => ({
    Session: {
      query: SessionQ,
      foreignKey: SessionQ.id,
      sourceKey: TutorAuditQ.session_id,
    },
    Auditor: {
      query: UserQ,
      foreignKey: UserQ.id,
      sourceKey: TutorAuditQ.auditor_id,
    },
  }),

  isExperienced: (): SQLFragment<boolean> =>
    SQL`${TutorAuditQ.audit_level} = ANY(${EXPERIENCED_AUDIT_LEVELS})`,

  isUsingAuditorRubricVersion: (): SQLFragment<boolean> =>
    SQL`(${TutorAuditQ.details}->'version' IS NOT NULL)`,

  getAuditsSinceLastLevelChange: (
    auditorId: number | SQLFragment<number>
  ): SQLFragment<TutorAuditAttributes> =>
    TutorAuditQ.where(
      { auditorId },
      SQL`${TutorAuditQ.created_at} > (
        SELECT COALESCE(MAX(${AuditorNoteQ.created_at}), '-INFINITY')
        ${AuditorNoteQ.where(
          {
            subjectUserId: auditorId,
          },
          SQL`${AuditorNoteQ.new_auditor_level} IS NOT NULL`
        )})`
    ),

  hasAuditLevel: (level: TutorAuditorLevel): SQLFragment<boolean> =>
    SQL`${TutorAuditQ.audit_level} = ${level}`,

  where: (
    {
      id,
      userId,
      auditorId,
      sessionId,
      asAuditor,
      isUsingAuditorRubricVersion = null,
      includeUser = false,
      includeAuditor = false,
      includeSession = false,
      include = [],
      groupBy = null,
      order = null,
      paranoid = true,
    }: {
      id?: number | null | SQLFragment<number>
      userId?: number | null | SQLFragment<number>
      auditorId?: number | null | SQLFragment<number>
      sessionId?: number | null | SQLFragment<number>
      asAuditor?: boolean | null
      isUsingAuditorRubricVersion?: boolean | null
      includeUser?: boolean
      includeAuditor?: boolean
      includeSession?: boolean
      include?: IncludeAssociations
      groupBy?: GroupBy | null
      order?: QueryOrder | null
      paranoid?: boolean
    },
    customWhere: SQLFragment<unknown> | null = null
  ): SQLFragment<TutorAuditAttributes> => SQL`
        FROM ${TutorAuditQ}
        ${includeAssociationsToSql(TutorAuditQ, include)}
        ${includeUser ? SQL`INNER JOIN ${UserQ} ON ${UserQ.id} = ${TutorAuditQ.user_id}` : SQL``}
        ${
          includeAuditor
            ? SQL`INNER JOIN ${UserQ} ON ${UserQ.id} = ${TutorAuditQ.auditor_id}`
            : SQL``
        }
        ${
          includeSession
            ? SQL`INNER JOIN ${SessionQ} ON ${SessionQ.id} = ${TutorAuditQ.session_id}`
            : SQL``
        }
        WHERE
            ${id != null ? SQL`${TutorAuditQ.id} = ${id} AND` : SQL``}
            ${userId != null ? SQL`${TutorAuditQ.user_id} = ${userId} AND` : SQL``}
            ${auditorId != null ? SQL`${TutorAuditQ.auditor_id} = ${auditorId} AND` : SQL``}
            ${sessionId != null ? SQL`${TutorAuditQ.session_id} = ${sessionId} AND` : SQL``}
            ${
              asAuditor != null
                ? SQL`${TutorAuditQ.is_peer_review} IS ${
                    asAuditor === true ? SQL`FALSE` : SQL`TRUE`
                  } AND`
                : SQL``
            }
            ${
              isUsingAuditorRubricVersion != null
                ? SQL`(
                  ${isUsingAuditorRubricVersion ? SQL`` : SQL`NOT`}
                  ${TutorAuditQ.isUsingAuditorRubricVersion()}
                ) AND`
                : SQL``
            }
            ${paranoid ? SQL`${TutorAuditQ.deleted_at} IS NULL AND` : SQL``}
            ${customWhere != null ? customWhere : SQL`TRUE`}
        ${queryGroupByToSQL(groupBy)}
        ${queryOrderToSQL(order)}
    `,

  getAuditor: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: TutorAuditQ.auditor_id,
    }),

  getVerifier: (): SQLFragment<UserAttributes> =>
    UserQ.where({
      id: TutorAuditQ.verifier_id,
    }),

  getDetailsExcludingInternal: (): SQLFragment<Record<string, unknown>> =>
    SQL`(${TutorAuditQ.details} - ${COACH_ESCALATION_SLUG} - 'internal-comment-1' - ${SAFETY_REPORT_SLUG} - 'safety-2')`,

  hasAuditRubricResponse: (key: string, value: AuditRubricResponse): SQLFragment<boolean> =>
    SQL`(${TutorAuditQ.details}->>${key} = ${value})`,

  escalatedToMentor: (): SQLFragment<boolean> =>
    SQL`(${TutorAuditQ.hasMarsResponse('mentor-1', ItemResponse.Yes)} OR
    ${TutorAuditQ.hasAuditRubricResponse(COACH_ESCALATION_SLUG, 'yes')})`,

  isSafeEnvironment: (): SQLFragment<boolean> =>
    TutorAuditQ.hasAuditRubricResponse(SAFETY_REPORT_SLUG, 'yes'),

  hasReply: (): SQLFragment<boolean> => SQL`${TutorAuditQ.reply_submitted_at} IS NOT NULL`,

  // Do not apply this to the new auditing rubric yet.
  hasPoorRating: (): SQLFragment<UserAttributes> => SQL`
        (
            (${TutorAuditQ.details} = '{}' AND
                (${TutorAuditQ.rating} = ${AuditRating.Novice} OR
                 ${TutorAuditQ.rating} = ${AuditRating.Flag})
            )
        OR
            (
              NOT(${TutorAuditQ.isUsingAuditorRubricVersion()}) AND
                (${TutorAuditQ.hasMarsResponse('respect-1', ItemResponse.No)} OR
                 ${TutorAuditQ.hasMarsResponse('mastery-1', ItemResponse.No)} OR
                (${TutorAuditQ.hasMarsResponse('active-1', ItemResponse.No)}::int +
                 ${TutorAuditQ.hasMarsResponse('active-2', ItemResponse.No)}::int +
                 ${TutorAuditQ.hasMarsResponse('active-3', ItemResponse.No)}::int) > 1)
            )
        )`,

  // This is from the deprecated MARS Rubric
  hasMarsResponse: (key: MarsKey, value: ItemResponse): SQLFragment<boolean> =>
    SQL`(${TutorAuditQ.details}->>${key} = ${value})`,

  // Count the number of sessions that have received at least one flag for the given MARS key.
  countFlaggedSessions: ({
    key,
    earliestDate,
    latestDate,
  }: {
    key: MarsKey
    earliestDate?: SQLFragment<Date>
    latestDate?: SQLFragment<Date>
  }): SQLFragment<number> =>
    SQL`COUNT(DISTINCT ${SessionQ.id})
    ${TutorAuditQ.where(
      { includeSession: true },
      SQL`
        ${TutorAuditQ.hasMarsResponse(key, ItemResponse.No)} AND
        ${earliestDate != null ? SQL`${SessionQ.start_date} >= ${earliestDate}` : SQL`TRUE`} AND
        ${latestDate != null ? SQL`${SessionQ.start_date} <= ${latestDate}` : SQL`TRUE`}`
    )}`,

  isFeatured: (): SQLFragment<boolean> => SQL`
        EXISTS(
            SELECT 1 ${TutorAuditFeaturedQ.where({
              tutorAuditId: TutorAuditQ.id,
            })}
        )
    `,

  needsReview: (): SQLFragment<boolean> => SQL`(
    (
      ${TutorAuditQ.hasAuditLevel(TutorAuditorLevel.Novice)}
      OR ${TutorAuditQ.hasAuditLevel(TutorAuditorLevel.Beginner)}
    )
    AND ${TutorAuditQ.verified_at} IS NULL
  )`,

  // Hide reviews from before August 1 because they aren't going to be used
  // anymore and create confusion for the coaches w.r.t how many reviews are left to verify
  isAfterCoachSystemLaunch: (): SQLFragment<boolean> =>
    SQL`${TutorAuditQ.created_at} > date '2023-08-01'`,

  isForSessionInWorld: ({ slug }: { slug: string }): SQLFragment<boolean> => SQL`
    EXISTS(SELECT 1 ${SessionQ.where(
      { id: TutorAuditQ.session_id },
      SQL`${SessionQ.isInWorld(slug)}`
    )})
  `,

  getPeerReviewsInWorld: ({
    worldSlug,
    userId,
  }: {
    worldSlug: string
    userId: number
  }): SQLFragment<number> =>
    TutorAuditQ.where({
      auditorId: userId,
      include: [
        {
          association: 'Session',
          required: true,
          include: [
            {
              association: 'World',
              required: true,
              where: SQL`${WorldQ.slug} = ${worldSlug}`,
            },
          ],
        },
      ],
    }),
}

export default TutorAuditQ
